import { useCallback, useEffect, useRef } from 'react';
import { pedigree } from './pedigree/layout';
import { config } from './pedigree/config';
import { pedigreeParallel } from './pedigree/layoutParallel';

// Distinguishes concurrent chart instances in the pool (instance-scoped cancellation) and in
// the dev telemetry — a second live chart quietly doubling the layout load is exactly the
// kind of thing the log line must expose.
let nextInstanceId = 0;

/**
 * Runs the pedigree layout in a Web Worker so a large/interconnected family
 * (best-of-both + bidirectional reorder search can take tens of seconds to
 * minutes) never freezes the main thread. Falls back to a synchronous
 * `pedigree()` call when Workers are unavailable (jsdom/Jest, SSR, or if the
 * worker fails to construct).
 *
 * Usage:
 *   const runLayout = useAsyncLayout();
 *   runLayout({ reqId, family, familyId, probandId, customPositions, prefs, spacing }, (res) => {
 *     if (res.reqId === currentReqId && res.ok) setLayout(res);
 *   });
 *
 * The caller owns request-id allocation and stale-result rejection (only act on
 * `res.reqId === <latest you issued>`); this hook also self-guards by replaying
 * only the most recent callback.
 *
 * NOTE (v1): the worker processes requests sequentially and is not interrupted
 * mid-layout — superseded requests are ignored on return but still occupy the
 * worker until they finish. Terminate-on-supersede is a future enhancement; the
 * main thread stays responsive regardless, which is the point.
 */
export function useAsyncLayout() {
  const workerRef = useRef(null);
  const readyRef = useRef(false);
  const deadRef = useRef(false); // worker errored -> permanently fall back to sync
  const reqRef = useRef(0); // latest reqId issued
  const cbRef = useRef(null); // latest callback
  const pendingRef = useRef(null); // request issued before the worker finished loading
  // B2: the parallel Web Worker pool (only when config.ENABLE_PARALLEL_LAYOUT). SHARED
  // across all chart instances (ref-counted singleton) so N live charts never spawn N pools.
  const poolRef = useRef(null);
  const poolLeaseRef = useRef(null); // { pool, release } from acquireLayoutPool
  const poolPendingRef = useRef(null); // request issued before the pool finished loading
  const debounceRef = useRef(null); // burst-coalescing timer for parallel dispatch
  const instanceIdRef = useRef(null);
  if (instanceIdRef.current == null) instanceIdRef.current = ++nextInstanceId;
  const parallel = config.ENABLE_PARALLEL_LAYOUT === true;

  const runSync = useCallback((payload, cb) => {
    try {
      const { family, familyId, probandId, customPositions, establishedOrder } =
        payload;
      const { nodes, edges, nodesById, simplifiedLayout, arrangeHint } =
        pedigree({
          family,
          familyId,
          probandId,
          customPositions,
          establishedOrder
        });
      cb({
        reqId: payload.reqId,
        ok: true,
        nodes,
        edges,
        nodesById,
        simplifiedLayout: !!simplifiedLayout,
        arrangeHint: !!arrangeHint
      });
    } catch (err) {
      cb({
        reqId: payload.reqId,
        ok: false,
        error: String(err && err.stack ? err.stack : err)
      });
    }
  }, []);

  // B2: dispatch a request through the parallel orchestrator + worker pool. Byte-identical to
  // sync (loadPerfParallelParity). Stale results are dropped on reqId; any failure falls back
  // to a synchronous layout so a chart always renders.
  const runParallel = useCallback(
    (payload, cb) => {
      // CANCEL SUPERSEDED WORK FIRST. The Canvas layout effect re-fires as inputs arrive at
      // open (saved order / pins / options), and a superseded request's minute-class search
      // tasks would otherwise run to completion — queueing the live request behind dead work
      // AND inflating every task via all-core contention (measured on F-1-01456: three stacked
      // relayouts → 160s, tasks 4× slower, final result 60-116s behind cancelled-in-spirit
      // work). Killing everything older than this request bounds the open at ~the cost of the
      // newest request alone.
      poolRef.current.cancelBelow(instanceIdRef.current, payload.reqId);
      // Per-task timing telemetry (dev only): each finished pool task reports its label,
      // worker-side compute ms, queue+clone total and the engine-vs-refine phase split —
      // logged as one line per layout so a slow open can be attributed (which rung/mode
      // dominates; search vs refine; queue wait vs compute; which chart instance).
      const t0 = Date.now();
      const timings = [];
      const executor = poolRef.current.executor({
        prefs: payload.prefs,
        spacing: payload.spacing,
        inst: instanceIdRef.current,
        gen: payload.reqId,
        onTask: (t) => timings.push(t)
      });
      pedigreeParallel(
        {
          family: payload.family,
          familyId: payload.familyId,
          customPositions: payload.customPositions,
          establishedOrder: payload.establishedOrder,
          orientationSeed: payload.orientationSeed
        },
        executor
      )
        .then((model) => {
          if (process.env.NODE_ENV !== 'production') {
            const parts = timings
              .map((t) => {
                const seed = t.phases?.seed
                  ? `; seed ${(t.phases.seed.seedMs / 1000).toFixed(1)}s/${
                      t.phases.seed.pipelineRuns
                    }runs, assign ${(t.phases.seed.assignMs / 1000).toFixed(
                      1
                    )}s, cont ${t.phases.seed.containment}, dec ${
                      t.phases.seed.decoupled
                    }${t.phases.seed.diverged ? ', DIVERGED' : ''}${
                      t.phases.seed.roleConflicts
                        ? `, ROLE-CONFLICTS ${t.phases.seed.roleConflicts}`
                        : ''
                    }`
                  : '';
                const refStats = t.phases?.refineStats
                  ? ` (${t.phases.refineStats.measures}ev/${t.phases.refineStats.accepted}acc)`
                  : '';
                const phase = t.phases
                  ? ` [v2 ${((t.phases.v2Ms || 0) / 1000).toFixed(
                      1
                    )}s, refine ${((t.phases.refineMs || 0) / 1000).toFixed(
                      1
                    )}s${refStats}${seed}]`
                  : '';
                const wait =
                  t.totalMs - t.ms > 500
                    ? ` (+${((t.totalMs - t.ms) / 1000).toFixed(1)}s wait)`
                    : '';
                return `${t.label} ${(t.ms / 1000).toFixed(1)}s${phase}${wait}`;
              })
              .join(', ');
            // eslint-disable-next-line no-console
            console.info(
              `[pedigree-layout] ${payload.familyId || ''} i${
                instanceIdRef.current
              }#${payload.reqId} parallel ${((Date.now() - t0) / 1000).toFixed(
                1
              )}s — ${parts || 'delegated to a single full layout'}`
            );
          }
          if (payload.reqId !== reqRef.current) return; // superseded
          cb({
            reqId: payload.reqId,
            ok: true,
            nodes: model.nodes,
            edges: model.edges,
            nodesById: model.nodesById,
            simplifiedLayout: !!model.simplifiedLayout,
            arrangeHint: !!model.arrangeHint
          });
        })
        .catch((err) => {
          // A cancellation rejection must NEVER fall through to the synchronous layout —
          // that would freeze the main thread for the very compute we just abandoned
          // (superseded request, or pool.terminate on unmount).
          const cancelled = String((err && err.message) || err).includes(
            'cancelled'
          );
          if (payload.reqId !== reqRef.current || cancelled) {
            if (process.env.NODE_ENV !== 'production') {
              // eslint-disable-next-line no-console
              console.info(
                `[pedigree-layout] ${payload.familyId || ''} i${
                  instanceIdRef.current
                }#${payload.reqId} superseded/cancelled after ${(
                  (Date.now() - t0) /
                  1000
                ).toFixed(1)}s`
              );
            }
            return;
          }
          runSync(payload, cb);
        });
    },
    [runSync]
  );

  // B2: spin up the worker pool when parallel layout is enabled. Dynamic import keeps
  // layoutPool (→ layoutWorkerClient's import.meta) out of any module Jest parses.
  useEffect(() => {
    if (!parallel || typeof Worker === 'undefined') return undefined;
    let alive = true;
    import('./layoutPool')
      .then((mod) => {
        if (!alive) return;
        const cores =
          (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) ||
          4;
        const size = Math.max(
          2,
          Math.min(config.PARALLEL_LAYOUT_MAX_WORKERS || 4, cores - 2)
        );
        try {
          // SHARED pool: all chart instances on the page use one ref-counted pool, so a
          // second live chart never doubles the worker count / concurrent search load.
          const lease = mod.acquireLayoutPool(size);
          if (lease.pool.size > 0) {
            poolLeaseRef.current = lease;
            poolRef.current = lease.pool;
            // Flush a request that arrived while the pool was loading.
            if (poolPendingRef.current) {
              const p = poolPendingRef.current;
              poolPendingRef.current = null;
              if (p.payload.reqId === reqRef.current)
                runParallel(p.payload, p.cb);
            }
          } else {
            lease.release();
            deadRef.current = true; // no workers -> sync fallback
          }
        } catch (e) {
          deadRef.current = true;
        }
      })
      .catch(() => {
        if (alive) deadRef.current = true;
      });
    return () => {
      alive = false;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      if (poolRef.current) {
        try {
          // Kill this instance's outstanding work, then drop the shared-pool lease (the
          // pool itself terminates only when the last instance releases it).
          poolRef.current.cancelInstance(instanceIdRef.current);
        } catch (e) {
          /* ignore */
        }
        poolRef.current = null;
      }
      if (poolLeaseRef.current) {
        try {
          poolLeaseRef.current.release();
        } catch (e) {
          /* ignore */
        }
        poolLeaseRef.current = null;
      }
    };
  }, [parallel, runParallel]);

  useEffect(() => {
    let alive = true;
    // Single-worker path: skipped when the parallel pool is in use.
    if (typeof Worker !== 'undefined' && !parallel) {
      // Dynamic import keeps `import.meta.url` (in layoutWorkerClient) out of any
      // module Jest loads — Jest's babel transform cannot parse import.meta.
      import('./layoutWorkerClient')
        .then((mod) => {
          if (!alive) return;
          let worker;
          try {
            worker = mod.createLayoutWorker();
          } catch (e) {
            deadRef.current = true; // construction failed -> sync from here on
            if (pendingRef.current) {
              const p = pendingRef.current;
              pendingRef.current = null;
              runSync(p.payload, p.cb);
            }
            return;
          }
          worker.onmessage = (e) => {
            // Only the most recent request's callback fires (drop stale results).
            if (e.data && e.data.reqId === reqRef.current && cbRef.current) {
              cbRef.current(e.data);
            }
          };
          worker.onerror = () => {
            deadRef.current = true;
            try {
              worker.terminate();
            } catch (e) {
              /* ignore */
            }
            workerRef.current = null;
            readyRef.current = false;
            if (pendingRef.current) {
              const p = pendingRef.current;
              pendingRef.current = null;
              runSync(p.payload, p.cb);
            }
          };
          workerRef.current = worker;
          readyRef.current = true;
          // Flush a request that arrived while the worker module was loading.
          if (pendingRef.current) {
            const p = pendingRef.current;
            pendingRef.current = null;
            cbRef.current = p.cb;
            worker.postMessage(p.payload);
          }
        })
        .catch(() => {
          if (alive) deadRef.current = true;
        });
    }
    return () => {
      alive = false;
      if (workerRef.current) {
        try {
          workerRef.current.terminate();
        } catch (e) {
          /* ignore */
        }
        workerRef.current = null;
      }
      readyRef.current = false;
    };
  }, [runSync, parallel]);

  const runLayout = useCallback(
    (payload, cb) => {
      reqRef.current = payload.reqId;
      cbRef.current = cb;
      // B2: parallel path. If the pool is ready, orchestrate across it; if it is still loading,
      // hold the request and flush on ready (so a large family never runs synchronously on the
      // main thread during the brief load window). Requires Workers — under jsdom/SSR the flag
      // is irrelevant and the synchronous fallback below runs as always.
      if (parallel && typeof Worker !== 'undefined' && !deadRef.current) {
        // COALESCE BURSTS: the open sequence re-fires the layout effect several times within
        // a short window (members → saved order → pin upgrade → options). A short debounce
        // merges sub-window re-fires into ONE dispatch carrying the newest inputs, so the
        // pool never even starts work the next re-fire would cancel. reqRef is already
        // bumped, so anything a stale timer would have dispatched is dropped here.
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          debounceRef.current = null;
          if (payload.reqId !== reqRef.current) return; // superseded while waiting
          if (poolRef.current) runParallel(payload, cb);
          else poolPendingRef.current = { payload, cb };
        }, 250);
        return;
      }
      if (readyRef.current && workerRef.current && !deadRef.current) {
        workerRef.current.postMessage(payload);
      } else if (
        typeof Worker !== 'undefined' &&
        !parallel &&
        !deadRef.current
      ) {
        // Worker still loading — hold the latest request; it posts once ready.
        pendingRef.current = { payload, cb };
      } else {
        runSync(payload, cb); // no Worker (Jest/SSR) or worker dead -> synchronous
      }
    },
    [runSync, runParallel, parallel]
  );

  return runLayout;
}
